import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import './FeatureLanding.css';
import { ImageResultDemo } from '../../components/demo';

const AiImageLandingPage = () => {
  const navigate = useNavigate();

  const faqData = [
    {
      q: '어떤 종류의 교육용 이미지를 만들 수 있나요?',
      a: '개념 설명, 색칠공부, 다이어그램, 포스터, 우리반 학급사진 카툰 변환 중에서 고를 수 있어요. "자유"에서는 플래시카드처럼 원하는 그림을 설명해 만들어요.'
    },
    {
      q: '색칠공부 도안도 AI로 만들 수 있나요?',
      a: '네, 주제를 적으면 흑백 색칠공부 도안을 그려요. 초등 수업이나 방과후 활동에 쓸 수 있어요.'
    },
    {
      q: '그림 느낌을 바꿀 수 있나요?',
      a: '네, "수채화 느낌으로", "귀여운 캐릭터풍으로"처럼 말하면 그 느낌으로 그려요. 만든 뒤에도 말로 고칠 수 있어요.'
    },
    {
      q: '참조 이미지를 올려서 비슷한 이미지를 만들 수 있나요?',
      a: '네, PRO는 참조 이미지를 최대 10장까지 올려 그 스타일이나 구도를 참고해 그릴 수 있어요.'
    },
    {
      q: '생성된 이미지를 PPT에 넣을 수 있나요?',
      a: '네, 이미지 파일로 받아 PPT, 학습지, 교실 게시물에 넣을 수 있어요.'
    },
    {
      q: '무료로 사용할 수 있나요?',
      a: '네, 가입만 하면 기본 모델로 이미지를 무료로 만들 수 있어요. 고급 모델과 참조 이미지(내 사진 참고)는 PRO 구독으로 이용하세요.'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "왓퀴즈 AI 이미지 생성",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web",
        "url": "https://whatquiz.co.kr/ai-image",
        "description": "교사 전용 AI 교육용 이미지 생성 도구. 색칠공부 도안, 다이어그램, 포스터, 플래시카드 등 수업에 필요한 이미지를 AI로 생성해요.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "KRW",
          "description": "무료로 시작, 고급 모델·참조 이미지는 PRO"
        },
        "creator": {
          "@type": "Organization",
          "name": "왓퀴즈",
          "url": "https://whatquiz.co.kr"
        }
      },
      {
        "@type": "HowTo",
        "name": "AI로 교육용 이미지 만드는 방법",
        "description": "왓퀴즈에서 AI로 수업용 이미지를 생성하는 3단계 방법",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "용도 고르기",
            "text": "개념 설명, 색칠공부, 다이어그램, 포스터, 우리반 학급사진, 자유 중에서 골라요."
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "내용 입력",
            "text": "원하는 그림을 말로 설명해요. PRO는 참조 이미지를 올릴 수 있어요."
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "받기",
            "text": "이미지 파일로 받아 PPT, 학습지, 교실 게시물에 써요."
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "왓퀴즈", "item": "https://whatquiz.co.kr" },
          { "@type": "ListItem", "position": 2, "name": "AI 이미지 생성", "item": "https://whatquiz.co.kr/ai-image" }
        ]
      }
    ]
  };

  const useCases = [
    { icon: 'lightbulb', title: '개념 설명', desc: '과학, 수학 개념을 단계별로 보여 주는 그림을 그려요' },
    { icon: 'crayon', title: '색칠공부', desc: '흑백 색칠공부 도안을 그려요. 초등 수업, 방과후 활동에 써요' },
    { icon: 'chart', title: '다이어그램', desc: '화살표와 라벨로 과정을 정리한 인포그래픽을 만들어요' },
    { icon: 'image', title: '포스터', desc: '교실 게시판에 붙일 포스터, 학급 규칙, 안내문을 만들어요' },
    { icon: 'camera', title: '우리반 학급사진', desc: '학급 사진을 카툰으로 바꾸고 문구를 넣어요' },
    { icon: 'cards', title: '자유', desc: '플래시카드처럼 원하는 그림을 말로 설명해 만들어요' },
  ];

  return (
    <div className="feature-landing">
      <Helmet>
        <title>AI 이미지 만들기 | 색칠공부·포스터·플래시카드 자동 생성 | 교사 전용 | 왓퀴즈</title>
        <meta name="description" content="색칠공부 도안, 개념 다이어그램, 교실 포스터, 플래시카드 등 교육용 이미지를 AI로 만들어요. 우리반 학급사진 카툰 변환까지. 교사 전용 무료 AI 이미지 생성." />
        <meta name="keywords" content="AI 이미지 만들기, 수업 이미지 만들기, 색칠공부 만들기, 플래시카드 만들기, 교육용 이미지 AI, 교실 포스터 만들기, AI 이미지 생성, 인포그래픽 만들기, 교사용 이미지 생성, 수업자료 이미지" />
        <link rel="canonical" href="https://whatquiz.co.kr/ai-image" />
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/ai-image" />
        <meta property="og:title" content="AI 이미지 만들기 | 교육용 이미지 자동 생성 | 왓퀴즈" />
        <meta property="og:description" content="색칠공부, 포스터, 플래시카드 등 교육용 이미지를 AI로 생성. 교사 전용 무료." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI 이미지 만들기 | 교육용 이미지 자동 생성 | 왓퀴즈" />
        <meta name="twitter:description" content="색칠공부, 포스터, 플래시카드 등 교육용 이미지를 AI로 생성. 교사 전용 무료." />

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* 히어로 */}
      <section className="feature-hero" style={{ background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)' }}>
        <div className="feature-hero-inner">
          <span className="feature-hero-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
            교사 전용 AI
          </span>
          <h1>말로 설명하면<br /><em>수업 그림</em>이 나와요</h1>
          <p className="feature-hero-desc">
            개념 설명, 색칠공부, 포스터까지 원하는 그림을 그려요.<br />
            우리반 학급사진을 카툰으로 바꿀 수도 있어요.
          </p>
          <button
            className="feature-hero-cta"
            onClick={() => navigate('/chat?category=image_generation')}
          >
            무료로 이미지 만들기 →
          </button>
        </div>
      </section>

      <section className="feature-section feature-section--demo">
        <div className="feature-section-inner feature-demo">
          <ImageResultDemo surface="ai_image" title="AI 이미지" />
        </div>
      </section>

      {/* 3단계 사용법 */}
      <section className="feature-section feature-section--gray">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">AI로 이미지를 어떻게 만드나요?</h2>
          <p className="feature-section-subtitle">3단계로 교육용 이미지가 완성돼요</p>
          <div className="feature-steps">
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>1</div>
              <h3>용도 고르기</h3>
              <p>개념 설명, 색칠공부, 다이어그램, 포스터, 우리반 학급사진, 자유 중에서 골라요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>2</div>
              <h3>내용 입력</h3>
              <p>원하는 그림을 말로 설명해요. PRO는 참조 이미지를 올릴 수 있어요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>3</div>
              <h3>받아서 쓰기</h3>
              <p>이미지 파일로 받아 PPT, 학습지, 교실 게시판에 써요.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 용도 */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">이런 그림을 만들어요</h2>
          <p className="feature-section-subtitle">이미지 화면에서 용도를 고르면 맞는 예시가 나와요</p>
          <div className="feature-grid">
            {useCases.map((uc, idx) => (
              <div key={idx} className="feature-card">
                <div className="feature-card-icon"><NamedGrayIcon name={uc.icon} /></div>
                <h3>{uc.title}</h3>
                <p>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">자주 묻는 질문</h2>
          <div className="feature-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="feature-faq-item">
                <h3 className="feature-faq-q">{faq.q}</h3>
                <p className="feature-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-bottom-cta">
        <h2>다음 수업 그림,<br />말 한 줄로 시작해 보세요</h2>
        <p>가입만 하면 무료로 시작할 수 있어요</p>
        <div className="feature-bottom-cta-buttons">
          <button className="feature-cta-primary" onClick={() => navigate('/chat?category=image_generation')}>
            무료로 이미지 만들기
          </button>
          <button className="feature-cta-secondary" onClick={() => navigate('/pricing')}>
            요금제 보기
          </button>
        </div>
      </section>

      {/* 관련 기능 */}
      <section className="feature-related">
        <div className="feature-related-inner">
          <h2>이런 기능도 있어요</h2>
          <div className="feature-related-grid">
            <div className="feature-related-card" onClick={() => navigate('/ai-ppt')}>
              <h3>AI PPT 만들기</h3>
              <p>주제만 적으면 수업 PPT, 글자도 바로 고쳐요</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/ai-worksheet')}>
              <h3>AI 학습지 만들기</h3>
              <p>문항 수와 유형대로 학습지와 정답지까지</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/create-saenggibu')}>
              <h3>AI 생기부 작성</h3>
              <p>한 번에 30명, 2026 기재요령 반영</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AiImageLandingPage;
